import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EvaluationResult } from '../models/quality.models';
import { LoadingStateComponent } from './loading-state.component';
import { EmptyStateComponent } from './empty-state.component';
import { ErrorStateComponent } from './error-state.component';
import { EvaluationScoreHistogramComponent } from './evaluation-score-histogram.component';

interface MetricScoreSummary {
  metric: string;
  average: number;
  count: number;
}

@Component({
  selector: 'app-evaluation-results-widget',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    LoadingStateComponent,
    EmptyStateComponent,
    ErrorStateComponent,
    EvaluationScoreHistogramComponent,
  ],
  templateUrl: './evaluation-results-widget.component.html',
  styleUrls: ['./evaluation-results-widget.component.scss'],
})
export class EvaluationResultsWidgetComponent {
  @Input() title = '';
  @Input() placeholder = '';
  @Input() results: EvaluationResult[] = [];
  @Input() loading = false;
  @Input() error = '';
  @Input() runDate = '';
  @Input() group = '';
  @Input() metric = '';
  @Input() groups: readonly string[] = [];
  @Input() metrics: readonly string[] = [];
  @Output() loadResults = new EventEmitter<void>();
  @Output() clear = new EventEmitter<void>();
  @Output() runDateChange = new EventEmitter<string>();
  @Output() groupChange = new EventEmitter<string>();
  @Output() metricChange = new EventEmitter<string>();
  average(): number {
    return this.results.length
      ? this.results.reduce((sum, item) => sum + item.score, 0) /
          this.results.length
      : 0;
  }
  highest(): EvaluationResult | undefined {
    return this.results.reduce(
      (best, item) => (!best || item.score > best.score ? item : best),
      undefined as EvaluationResult | undefined
    );
  }
  lowest(): EvaluationResult | undefined {
    return this.results.reduce(
      (best, item) => (!best || item.score < best.score ? item : best),
      undefined as EvaluationResult | undefined
    );
  }
  uniqueExplanations(): string[] {
    return [
      ...new Set(
        this.results
          .map((result) => result.explanation?.trim())
          .filter((explanation): explanation is string => Boolean(explanation))
      ),
    ];
  }

  metricScores(): MetricScoreSummary[] {
    const scores = new Map<string, number[]>();
    this.results.forEach((result) => {
      const values = scores.get(result.metricName) ?? [];
      values.push(result.score);
      scores.set(result.metricName, values);
    });
    return [...scores.entries()]
      .map(([metric, values]) => ({
        metric,
        average: values.reduce((sum, score) => sum + score, 0) / values.length,
        count: values.length,
      }))
      .sort((a, b) => a.average - b.average);
  }
}
